import {type FC, useCallback, useState} from 'react';

import NiceModal from '@ebay/nice-modal-react';
import {runInAction} from 'mobx';
import {Observer} from 'mobx-react';

import type {AccountApiModel as Account} from '@yourcoach/shared/api/account';
import type {TextInputChangeEventAction} from '@yourcoach/shared/uikit/TextInput/types';
import type {GetComponentProps} from '@yourcoach/shared/utils/utility-types';

import {useI18n} from '@yourcoach/i18n';
import {SelectCountryModal} from '@yourcoach/shared/modules/select-country';
import {PhoneInput} from '@yourcoach/shared/uikit/PhoneInput';
import {TextInput} from '@yourcoach/shared/uikit/TextInput';
import {eventPayloadActionCreator} from '@yourcoach/shared/utils/events';
import {useMobxStore} from '@yourcoach/shared/utils/store';
import {getEmailSchema} from '@yourcoach/shared/utils/validation';
import {createTextInputField} from '@yourcoach/shared/utils/validation/createTextInputField';

import type {Props} from './types';

import * as S from './styles';

export const AccountForm: FC<Props> = ({
  inProgress,
  onSubmit,
  type: typeProp,
}) => {
  const {t} = useI18n();
  const [type, setType] = useState<Account['type']>(typeProp || 'email');

  const fields = useMobxStore(() => ({
    email: createTextInputField('email', {
      validationRule: getEmailSchema(),
    }),
    phone: createTextInputField('phone', {}),
  }));

  const onSelectCountryClick: GetComponentProps<
    typeof PhoneInput
  >['onSelectCountryClick'] = useCallback(async setCountry => {
    const country: SelectCountryModal.Resolve =
      await NiceModal.show(SelectCountryModal);

    if (country) {
      setCountry(country.iso2);
    }
  }, []);

  const onPhoneChange: GetComponentProps<typeof PhoneInput>['onPhoneChange'] =
    useCallback(
      (phone, isValid, isEmpty) => {
        runInAction(() => {
          fields.phone.setValue(isEmpty ? '' : phone);
          fields.phone.isValid = isValid;
          fields.phone.error = null;

          if (isEmpty) {
            fields.phone.error = t('shared:validation.phone_required');
            fields.phone.isValid = false;
          } else if (!isValid) {
            fields.phone.error = t('shared:validation.phone_not_valid');
          }
        });
      },
      [fields, t],
    );

  const onSendCodeButtonClick = useCallback(async () => {
    let isValid = false;

    if (type === 'phone') {
      isValid = Boolean(fields.phone.isValid);
    } else {
      isValid = fields.email.validate();
    }

    if (isValid) {
      onSubmit({[type]: fields[type].value});
    }
  }, [type, fields, onSubmit]);

  const emailChangeHandler = (action: TextInputChangeEventAction) => {
    fields.email.onChange?.(
      eventPayloadActionCreator(
        {
          text: action.payload?.text.trim() || '',
        },
        action.event,
      ),
    );
  };

  return (
    <S.Container>
      <S.Tabs>
        <S.Tab isActive={type === 'email'} onClick={() => setType('email')}>
          {t('web-app:Login.sign_in.email_label')}
        </S.Tab>
        <S.Tab isActive={type === 'phone'} onClick={() => setType('phone')}>
          {t('web-app:Login.sign_in.phone_label')}
        </S.Tab>
      </S.Tabs>
      <S.Description>
        {
          /**
           * @i18n_parser
           *
           * t('web-app:Login.sign_in.enter_phone_label')
           * t('web-app:Login.sign_in.enter_email_label')
           */
          t(`web-app:Login.sign_in.enter_${type ? 'phone' : 'email'}_label`)
        }
      </S.Description>
      <Observer>
        {() =>
          type === 'phone' ? (
            <PhoneInput
              autoFocus
              disabled={inProgress}
              error={fields.phone.error || ''}
              onPhoneChange={onPhoneChange}
              onSelectCountryClick={onSelectCountryClick}
            />
          ) : (
            <TextInput
              autoFocus
              disabled={inProgress}
              error={fields.email.error || ''}
              onChange={emailChangeHandler}
              placeholder={t('web-app:Login.sign_in.email_placeholder')}
              value={fields.email.value}
            />
          )
        }
      </Observer>
      <Observer>
        {() => (
          <S.SendCodeButton
            isLoaderReplaceContent
            isLoading={inProgress}
            onClick={onSendCodeButtonClick}
          >
            {t('web-app:Login.sign_in.send_code_button')}
          </S.SendCodeButton>
        )}
      </Observer>
    </S.Container>
  );
};
